import {
  Box,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { ChangeEvent, ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export interface Option {
  value: string;
  label: ReactNode;
}

export interface ApprovalCardProps {
  title: string;
  isApproved: boolean | null;
  onApprovalChange: (isApproved: boolean) => void;
  options?: Option[];
  selectedOption?: string;
  onOptionSelect?: (selectedValue: string) => void;
  selectionOnly?: boolean;
}

export function ApprovalForm({
  title,
  isApproved,
  onApprovalChange,
  options,
  selectedOption,
  onOptionSelect,
  selectionOnly,
}: ApprovalCardProps): ReactElement {
  const { t } = useTranslation();

  const hasAlternatives = options && options.length > 1;
  const showOptionSelector = hasAlternatives && !isApproved && isApproved !== null && !!onOptionSelect;

  function handleApprovalChange(e: ChangeEvent<HTMLInputElement>) {
    onApprovalChange(e.target.value === 'true');
  }

  function handleOptionChange(e: SelectChangeEvent<string>) {
    if (onOptionSelect) {
      onOptionSelect(e.target.value);
    }
  }

  if (selectionOnly) {
    return (
      <Box sx={{ padding: 2 }}>
        <Select
          fullWidth
          displayEmpty
          MenuProps={{ disableScrollLock: true }}
          value={selectedOption ?? ''}
          onChange={handleOptionChange}
          size='small'
        >
          <MenuItem value='' disabled>
            {t('selfService.materialSelection')}
          </MenuItem>
          {options?.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 2, justifyContent: 'center' }}>
      <Typography variant='body1' textAlign='center' sx={{ marginBottom: 1 }}>
        {t(title)}
      </Typography>
      <RadioGroup
        name={`approval-${title}`}
        onChange={handleApprovalChange}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          marginBottom: showOptionSelector ? 2 : 0,
        }}
        value={isApproved}
      >
        <FormControlLabel
          value={true}
          control={<Radio />}
          label={t('common.yes')}
          sx={{ '& .MuiFormControlLabel-label': { fontSize: '1rem' } }}
        />
        <FormControlLabel
          value={false}
          control={<Radio />}
          label={t('common.no')}
          sx={{ '& .MuiFormControlLabel-label': { fontSize: '1rem' } }}
        />
      </RadioGroup>
      {showOptionSelector && (
        <Select
          fullWidth
          MenuProps={{ disableScrollLock: true }}
          value={selectedOption}
          onChange={handleOptionChange}
          size='small'
        >
          {options!.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      )}
    </Box>
  );
}
